"use client"
import Text from "@/components/common/Typography/Text"
import { Card, Col, Popover, Row } from "antd"
import { FC, memo, ReactNode, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"

import InputSearch, { InputSearchRef } from "./InputSearch"
import { EnumHotelSearchType, HotelLocation } from "@/lib/redux/features/hotel/type"
import HotelSearchDestinationResult from "./HotelSearchDestinationResult"
import { generateAddressHotel } from "@/utils/hotel/generateAddressHotel"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { hotelActions } from "@/lib/redux/features/hotel/reduce"

type SelectPlacementProps = {
  label: string,
  icon: ReactNode,
  placeholder: string,
  value?: HotelLocation,
  onSearch?: (searchValue?: string) => void,
  onChange?: (airport?: HotelLocation) => void,
  size?: 'small' | 'default',
}

const SelectPlacement: FC<SelectPlacementProps> = ({ size = 'default', value, onChange, onSearch, ...props }) => {
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation()
  const { getTranslatedText } = useTETranslation()
  const ref = useRef<HTMLDivElement>(null)
  const refInputSearch = useRef<InputSearchRef>(null)
  const dispatch = useAppDispatch()
  const typeSearch = useAppSelector(state => state.hotel.typeSearch)

  const isSmall = size === 'small'

  return (
    <Popover
      trigger={['click']}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) {
          dispatch(hotelActions.updateSearchingDate(false))
          dispatch(hotelActions.setPopularDestinations({ show: typeSearch }))
        }
      }}
      arrow={false}
      placement='bottomLeft'
      classNames={{
        root: "home__searching--destination-popover",
      }}
      styles={{
        content: {
          padding: 0,
          width: "55rem",
          maxHeight: "50rem",
          overflow: "hidden",
        },
      }}
      content={
        open ? (
          <HotelSearchDestinationResult
            onChange={(hotel?: HotelLocation) => {
              if (!hotel) return
              onChange?.(hotel)
              setOpen(false)
            }}
          />
        ) : null
      }
    >
      {isSmall ? (
        <div
          ref={ref}
          className="home__searching--field-control home__searching--field-control--stacked"
          onClick={() => setOpen(true)}
        >
          <Text className="home__searching--field-label">{props.label}</Text>
          <div className="home__searching--field-control-row">
            <span className="home__searching--field-icon" aria-hidden>
              {props.icon}
            </span>
            <InputSearch
              ref={refInputSearch}
              style={{ padding: 0 }}
              placeholder={props.placeholder}
              className="home__searching--input home__searching--input-small"
              variant="borderless"
              cardSize={size}
              value={getTranslatedText(value?.nameLang?.vi, value?.nameLang?.en)}
              onSearch={onSearch}
              location={value}
              allowClear
            />
            <FontAwesomeIcon icon={faChevronDown} className="home__searching--field-chevron" aria-hidden />
          </div>
        </div>
      ) : (
        <Card
          ref={ref}
          size='small'
          variant='borderless'
          className="home__searching--placement"
          onClick={() => setOpen(true)}
        >
          <Row className="home__searching--input-label" align='middle' gutter={[5, 5]} wrap={false}>
            <Col>
              <Text>
                {props.icon}
              </Text>
            </Col>
            <Col>
              <Text>{props.label}</Text>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ paddingBlock: '0.5rem' }}>
              <InputSearch
                ref={refInputSearch}
                style={{ paddingLeft: 0 }}
                placeholder={props.placeholder}
                className="home__searching--input"
                variant='borderless'
                cardSize={size}
                value={getTranslatedText(value?.nameLang?.vi, value?.nameLang?.en)}
                onSearch={onSearch}
                location={value}
                allowClear
              />
            </Col>
          </Row>
          <Row>
            <Text ellipsis type='secondary' style={!!value?.id ? {} : { visibility: 'hidden', opacity: 0 }}>
              {value?.searchType === EnumHotelSearchType.HOTEL
                ? generateAddressHotel(value, getTranslatedText)
                : `${value?.activeHotel || 0} ${t("common.hotel")}`}
            </Text>
          </Row>
        </Card>
      )}
    </Popover>
  )
}

export default memo(SelectPlacement)